// src/pages/IO/ExportPDF.tsx

/*

testen, ob sensorIds teil der url werden können, um schnelleren aufruf zu ermöglichen, bzgl. gleich bei step 3 zu landen

const queryParams = new URLSearchParams({
  sensorIds: reportSensors,
}).toString()

navigate(`?${queryParams}`)

*/

import {
  ArrowLeftTwoTone,
  ArrowRightTwoTone,
  ImportExportTwoTone,
  MoreVertTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Avatar,
  Badge,
  Breadcrumbs,
  Button,
  Chip,
  Collapse,
  Container,
  Divider,
  IconButton,
  Input,
  List,
  ListSubheader,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { ContractPool } from '@/types/domain/contract'
import { FolderId, FolderName, FolderPool } from '@/types/domain/folder'
import { SensorId, SensorPool } from '@/types/domain/sensor'

import {
  categoryByType,
  getStorage,
  isFilterMatch,
  setStorage,
  userLocales,
} from '../../api'
import {
  AM,
  DlgReportResult,
  FactorWarning,
  HintSelectFolder,
  HintSelectSensor,
  HStack,
  KPage,
  Preflight,
  Preloader,
  ReportFolderSelector,
  ReportNotAvailable,
  SectionAbo,
  SectionAdHoc,
  SensorItem,
  usePreloader,
} from '../../components/'
import {
  useFactorApi,
  useFolderPool,
  useSensorPool,
  useFolderName,
  useReportApi,
  useOperator,
  useContractPool,
  OperatorData,
} from '../../hooks'
import useRenderCount from '../../hooks/tools/useRenderCount'

type Props = {
  PAGE_CONFIG?: string
}

export const ExportPDF = ({
  PAGE_CONFIG = 'settings_report_generator',
}: Props) => {
  // render performance
  const rc = useRenderCount()
  console.log('ExportPDF', rc)

  const location = useLocation()

  const { isLoading } = usePreloader()

  const fetchedRef = useRef(new Set())

  const navigate = useNavigate()
  const [sensorFactors, setSensorFactors] = useState([])

  const { getAll: getSensorFactor } = useFactorApi()

  const theme = useTheme()
  const backgroundColor = theme.palette.success.main
  const borderColor = theme.palette.lightgray.dark
  const border = `solid 1px ${borderColor}`

  const { folder_id } = useParams()
  const fId = folder_id ? Number(folder_id) : 0

  const [config, setConfig] = useState(getStorage(PAGE_CONFIG))
  const [actionMenu, showActionMenu] = useState(null)

  // stepper wizard
  const STEP1_FOLDER: number = 0
  const STEP2_SENSORS: number = 1
  const STEP3_MODE: number = 2

  const [step, setStep] = useState<number>(STEP1_FOLDER)

  // STEP 1 (folder)

  const fName: FolderName = useFolderName(fId)

  // eligible sensors = consumption sensors in selected folder
  const [eligibleSensors, setEligibleSensors] = useState([])

  // STEP 2 (sensors)
  const [reportSensors, setReportSensors] = useState([])

  // STEP 3 (mode: ad-hoc | abo)
  const MODE_ADHOC: number = 0
  const MODE_ABO: number = 1

  const [mode, setMode] = useState<number>(MODE_ADHOC)

  // ad-hoc related
  const [reportDate, setReportDate] = useState<Date>(
    new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)
  )

  const options: Intl.DateTimeFormatOptions = {
    month: 'long',
    year: 'numeric',
  }

  const period = reportDate.toLocaleString(userLocales, options)
  const [showDownload, setShowDownload] = useState<boolean>(false)

  // contract related
  const [contractBegin, setContractBegin] = useState<Date | null>(null)
  const [contractExpire, setContractExpire] = useState<Date | null>(null)

  // abo related
  const [aboData, setAboData] = useState(null)
  const [recipientsList, setRecipientsList] = useState([])

  const [problematic, setProblematic] = useState([])
  const [newP, setNewP] = useState([])

  // #######################################
  // # hooks section (sort alphabetically) #
  // #######################################

  const cPool: ContractPool = useContractPool()
  const fPool: FolderPool = useFolderPool()
  const opData: OperatorData = useOperator()
  const sPool: SensorPool = useSensorPool()

  // ###################
  // # helpers section #
  // ###################

  const NO_FOLDER_SELECTED: boolean = fId === 0
  const NO_CONSUMPTION_SENSORS: boolean = eligibleSensors.length === 0
  const NO_REPORT_SENSORS: boolean = reportSensors.length === 0
  const NO_ABO: boolean = aboData === null
  const NO_ABO_RECIPIENTS: boolean = recipientsList.length === 0

  const ALL_SENSORS_SELECTED: boolean =
    eligibleSensors.length === reportSensors.length

  const INVALID_FOLDERID: boolean = !fPool.find(f => Number(f.id) === fId)

  const isStep1: boolean = step === STEP1_FOLDER
  const isStep2: boolean = step === STEP2_SENSORS
  const isStep3: boolean = step === STEP3_MODE

  const isAdHocMode: boolean = mode === MODE_ADHOC
  const isAboMode: boolean = mode === MODE_ABO

  // ###########################################
  // # functions section (sort alphabetically) #
  // ###########################################

  const {
    createSettings,
    deleteSettings,
    generatePdf,
    isError,
    status,
    updateSettings,
  } = useReportApi()

  const generateSelected = list =>
    generatePdf(fId, reportDate.getMonth() + 1, reportDate.getFullYear(), list)

  const gotoNextStep = () => setStep(prev => prev + 1)

  const gotoPrevStep = () => {
    setStep(prev => {
      if (prev === STEP2_SENSORS) {
        const qp = new URLSearchParams(location.search)
        if (qp.has('selected')) {
          qp.delete('selected')
          const rest = qp.toString()
          navigate({ search: rest ? `?${rest}` : '' }, { replace: true })
        }
        return STEP1_FOLDER
      }
      return prev - 1
    })
  }

  const onChangeFolder = (id: FolderId) => navigate(`/io/export-pdf/${id}`)

  const onChangeTab = (event, tab: number) => {
    setMode(tab)
    setConfig(prev => ({ ...prev, tab }))
  }

  const onCloseActionMenu = () => showActionMenu(null)

  const onDeleteAbo = () => {
    deleteSettings(fId)
    setRecipientsList([])
    setAboData(null)
  }

  const onShowActionMenu = event => showActionMenu(event.currentTarget)

  const onUpdateAbo = () => {
    const recipients = recipientsList
      .map(subscriber => subscriber.email)
      .join(',')
    const sensorIds = reportSensors.join(',')

    const create = () => createSettings(fId, recipients, sensorIds)
    const update = () => updateSettings(fId, recipients, sensorIds)

    NO_ABO ? create() : update()
  }

  // ###################
  // # useMemo section #
  // ###################

  const URLselectedSensors = useMemo(() => {
    const queryParams = new URLSearchParams(location.search)
    return (
      queryParams
        .get('selected')
        ?.split(',')
        .map(item => Number(item)) || []
    )
  }, [location.search])

  const validURLSelection = useMemo(() => {
    if (!URLselectedSensors?.length || eligibleSensors.length === 0) return []
    const set = new Set(eligibleSensors.map(s => Number(s.entity_id)))
    return URLselectedSensors.filter(id => set.has(id))
  }, [URLselectedSensors, eligibleSensors])

  // ######################
  // # useEffects section #
  // ######################

  useEffect(() => setStorage(PAGE_CONFIG, config), [config, PAGE_CONFIG])

  // update sensor factors on report sensors change
  useEffect(() => {
    if (reportSensors.length === 0 || step !== STEP3_MODE) return

    const toFetch = reportSensors.filter(id => !fetchedRef.current.has(id))
    if (toFetch.length === 0) return

    const fetchFactors = async () => {
      const factors = await Promise.all(toFetch.map(s => getSensorFactor(s)))

      toFetch.forEach(id => fetchedRef.current.add(id))

      setSensorFactors(prev => {
        const next = [...prev, ...factors.flat()]
        const seen = new Set()

        return next.filter(item => {
          const key = item?.id ?? item?.entity_id ?? JSON.stringify(item)
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
      })
      setProblematic([])
    }

    fetchFactors()
  }, [getSensorFactor, reportSensors, step])

  useEffect(() => {
    const NO_SENSORS = sPool.length === 0

    if (!opData || NO_SENSORS) return

    const SORTBY_TYPE_IN_FOLDER = (a, b) =>
      a.type_in_folder > b.type_in_folder ? 1 : -1
    const CONSUMPTION_SENSOR = f =>
      f.sensor_target === 'consumption' ||
      f.sensor_target === 'performance' ||
      categoryByType(f.sensor_type, f.type_in_folder) === 'performance'
    const CURRENT_FOLDER = f => f.folder_id === fId
    const REMOVE_UNDEFINED = f => f !== undefined
    const FLATTEN = f => f.attributes

    const prepareConsumptionSensors = sensorIds => {
      if (!sensorIds) return

      const sensors = [
        ...new Set(sensorIds.map(id => sPool.find(f => Number(f.id) === id))),
      ]
        .filter(REMOVE_UNDEFINED)
        .map(FLATTEN)
        .filter(CURRENT_FOLDER)
        .filter(CONSUMPTION_SENSOR)
        .sort(SORTBY_TYPE_IN_FOLDER)

      setEligibleSensors(sensors)
      setReportSensors(sensors.map(s => Number(s.entity_id)))
    }

    const prepareReports = reports => {
      if (!reports) return

      const rRecord = reports.find(CURRENT_FOLDER)

      if (!rRecord) {
        setAboData(null)
        return
      }

      setAboData(rRecord)

      const recipients = rRecord.mailto.split(',').map(email => ({ email }))
      setRecipientsList(recipients)

      setMode(MODE_ABO)
    }

    const prepareSensors = () => {
      const { reports, sensors: sensorIds } = opData

      prepareConsumptionSensors(sensorIds)
      prepareReports(reports)
      setProblematic([])
    }

    prepareSensors()
  }, [opData, sPool, fId])

  // update contract period on folder change
  useEffect(() => {
    const NO_CONTRACTS: boolean = cPool?.length === 0
    const UNKNOWN_FOLDER: boolean = fName === ''
    if (UNKNOWN_FOLDER || NO_CONTRACTS) return

    const setContractPeriod = (name: string, pool: ContractPool) => {
      const FILTER_NAME = f => f.attributes.name === name
      const cRecord = pool.find(FILTER_NAME)
      if (!cRecord) return

      const begin = String(cRecord?.attributes?.date_begin).split('-')
      const expire = String(cRecord?.attributes?.date_expire).split('-')

      setContractBegin(
        new Date(Number(begin[0]), Number(begin[1]) - 1, Number(begin[2]))
      )
      setContractExpire(
        new Date(Number(expire[0]), Number(expire[1]) - 1, Number(expire[2]))
      )
    }

    setContractPeriod(fName, cPool)
  }, [fName, cPool])

  // if status includes the "https://"" we have a pdf download link: show that link
  useEffect(() => {
    if (!status?.includes('https://')) return

    setShowDownload(true)
  }, [status])

  // hide pdf download link when report date changes or step changes
  useEffect(() => setShowDownload(false), [reportDate, step])

  useEffect(() => {
    const qp = new URLSearchParams(location.search)

    if (step === STEP1_FOLDER) {
      // selected entfernen
      if (qp.has('selected')) {
        qp.delete('selected')
        const rest = qp.toString()
        navigate({ search: rest ? `?${rest}` : '' }, { replace: true })
      }
      return
    }

    const next = reportSensors.join(',') // echte Kommas
    // andere Params behalten, selected nicht via URLSearchParams schreiben
    const copy = new URLSearchParams(qp)
    copy.delete('selected')
    const rest = copy.toString()

    const search = next
      ? rest
        ? `?${rest}&selected=${next}`
        : `?selected=${next}`
      : rest
        ? `?${rest}`
        : ''

    if (`?${qp.toString()}` !== search) navigate({ search }, { replace: true })
  }, [step, reportSensors, location.search, navigate])

  useEffect(() => {
    if (step !== STEP1_FOLDER) return
    if (validURLSelection.length === 0) return
    setReportSensors(validURLSelection)
    setStep(STEP3_MODE)
  }, [step, validURLSelection])

  const [filter, setFilter] = useState<string>(config.filter ?? '')

  const onChangeFilter = event => setFilter(event.target.value)

  const FILTER_SEARCH = item =>
    filter
      ? isFilterMatch(filter, item.name) ||
        isFilterMatch(filter, item.meter_number) ||
        isFilterMatch(filter, item.device_id) ||
        Number(filter) === Number(item.entity_id)
      : item

  // #############
  // # component #
  // #############

  return (
    <>
      {/* preloader */}
      <Preloader isLoading={isLoading} />
      {/* preloader end */}

      {/* preloader */}
      <Preloader
        caption={'Bericht wird generiert ...'}
        error={isError && isError}
        isLoading={status === 'Bericht wird generiert...'}
      />
      {/* preloader end */}

      {/* action menu */}
      <AM
        anchorEl={actionMenu}
        caption={'Berichte'}
        open={Boolean(actionMenu)}
        onClose={onCloseActionMenu}
      >
        <Divider />
      </AM>
      {/* action menu end */}

      {!isLoading && (
        <KPage
          action={
            <IconButton onClick={onShowActionMenu}>
              <MoreVertTwoTone />
            </IconButton>
          }
          avatar={
            <Avatar>
              <ImportExportTwoTone />
            </Avatar>
          }
          title={
            <Breadcrumbs sx={{ mx: 1, my: 2 }}>
              <Link to={'/io'}>
                <Typography>Exporte</Typography>
              </Link>
              <Typography>Gebäude Berichte</Typography>
            </Breadcrumbs>
          }
        >
          <Stepper activeStep={step} orientation={'vertical'} sx={{ mb: 10 }}>
            {['Gebäude', 'Sensoren', 'Modus'].map((item, key) => (
              <Step key={item}>
                <StepLabel>
                  {item} {isStep1 && key === 0 && 'wählen ...'}
                  {step > STEP1_FOLDER && key === 0 && fName}
                  {isStep2 && key === 1 && 'wählen ...'}
                  {step > STEP1_FOLDER && key === 1 && (
                    <Chip
                      label={`${reportSensors.length} / ${
                        eligibleSensors.length
                      }`}
                      sx={{ ml: 1 }}
                    />
                  )}
                </StepLabel>

                <StepContent>
                  {/* Step 1 : Gebäude */}
                  <Collapse in={isStep1} sx={{ my: 1 }}>
                    <ReportFolderSelector id={fId} onChange={onChangeFolder} />

                    <Collapse
                      in={INVALID_FOLDERID && !NO_FOLDER_SELECTED}
                      sx={{ mx: 1, mb: 1 }}
                    >
                      <Alert severity={'error'}>
                        <AlertTitle>Nicht verfügbar</AlertTitle>
                        Das Gebäude mit der ID <strong>{fId}</strong> wurde
                        nicht gefunden.
                      </Alert>
                    </Collapse>

                    <ReportNotAvailable
                      id={fId}
                      condition={
                        !INVALID_FOLDERID &&
                        !NO_FOLDER_SELECTED &&
                        NO_CONSUMPTION_SENSORS
                      }
                    />

                    <Collapse in={NO_FOLDER_SELECTED}>
                      <HintSelectFolder />
                    </Collapse>
                  </Collapse>

                  {/* Sensoren */}
                  <Collapse in={isStep2}>
                    <List
                      sx={{
                        border,
                        borderRadius: '10px',
                        m: 1,
                        maxHeight: 200,
                        minHeight: `calc(100vh - 600px)`,
                        overflow: 'auto',
                        py: 0,
                      }}
                    >
                      <ListSubheader sx={{ py: 0 }}>
                        <HStack>
                          <Input
                            fullWidth
                            onChange={onChangeFilter}
                            placeholder={'Filter'}
                            sx={{ py: 1 }}
                            value={filter}
                          />

                          <Divider
                            orientation={'vertical'}
                            sx={{ height: 30 }}
                          />
                          <Tooltip title={'Massen Auswahl'}>
                            <Switch
                              checked={ALL_SENSORS_SELECTED}
                              disabled={filter !== ''}
                              onChange={() => {
                                if (ALL_SENSORS_SELECTED) {
                                  setReportSensors([])
                                } else {
                                  setReportSensors(
                                    eligibleSensors.map(s =>
                                      Number(s.entity_id)
                                    )
                                  )
                                }
                              }}
                            />
                          </Tooltip>
                        </HStack>
                      </ListSubheader>

                      {eligibleSensors.filter(FILTER_SEARCH).map(s => {
                        const sId = Number(s.entity_id)
                        const selected = reportSensors.includes(sId)

                        const onToggle = id =>
                          setReportSensors(prev =>
                            prev.includes(id)
                              ? prev.filter(f => Number(f) !== id)
                              : [...prev, id]
                          )

                        const onClick = () => onToggle(sId)

                        return (
                          <SensorItem
                            id={sId}
                            key={sId}
                            onClick={onClick}
                            selected={selected}
                          />
                        )
                      })}
                    </List>

                    <Collapse in={NO_REPORT_SENSORS}>
                      <HintSelectSensor />
                    </Collapse>
                  </Collapse>

                  {/* Modus */}
                  <Collapse in={isStep3}>
                    <Stack>
                      <Tabs
                        centered
                        indicatorColor={'primary'}
                        onChange={onChangeTab}
                        sx={{ mb: 2 }}
                        textColor={'primary'}
                        value={mode}
                      >
                        <Tab label={'ad-hoc Report'} value={0} />
                        <Tab
                          label={
                            <Badge
                              anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                              }}
                              badgeContent={aboData ? 'aktiv' : null}
                              slotProps={{ badge: { sx: { backgroundColor } } }}
                              color={'secondary'}
                              overlap={'circular'}
                            >
                              Abonnement
                            </Badge>
                          }
                          value={1}
                        />
                      </Tabs>

                      {/* ad-hoc */}
                      <Collapse in={isAdHocMode}>
                        <SectionAdHoc
                          reportDate={reportDate}
                          contractBegin={contractBegin}
                          contractExpire={contractExpire}
                          onChange={date => setReportDate(date)}
                        />

                        <Collapse in={true}>
                          <Preflight
                            reportDate={reportDate}
                            sensors={reportSensors}
                            factors={sensorFactors}
                            onChange={problematic => setNewP(problematic)}
                          />
                        </Collapse>

                        {problematic?.map((id: SensorId) => (
                          <FactorWarning
                            date={reportDate}
                            key={id}
                            factors={sensorFactors}
                            id={id}
                          />
                        ))}

                        {/* Status */}
                        <Collapse
                          in={
                            status !== undefined &&
                            !NO_FOLDER_SELECTED &&
                            step > STEP2_SENSORS
                          }
                          sx={{ mt: 5 }}
                        >
                          {status && (
                            <>
                              {showDownload === true &&
                              status?.includes('https://') ? (
                                <DlgReportResult
                                  severity={'success'}
                                  status={status}
                                  title={`Ad-hoc Bericht für ${period} für ${fName} erstellt.`}
                                />
                              ) : null}
                              {isError && (
                                <DlgReportResult
                                  severity={'error'}
                                  status={isError}
                                  title={`Report ${fName} für ${period} konnte nicht erstellt werden. Bitte wenden Sie sich an Ihren Coach.`}
                                />
                              )}
                            </>
                          )}
                        </Collapse>
                      </Collapse>

                      {/* abonnement */}
                      <SectionAbo
                        data={recipientsList}
                        mode={mode}
                        setRecipientsList={setRecipientsList}
                      />
                    </Stack>
                  </Collapse>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          <Container
            maxWidth={'xl'}
            sx={{ position: 'absolute', bottom: '100px', right: '60px' }}
          >
            <HStack justifyContent={'flex-end'}>
              <Button disabled={isStep1} onClick={gotoPrevStep}>
                <ArrowLeftTwoTone /> Zurück
              </Button>

              {step !== STEP3_MODE && (
                <Button
                  disabled={
                    NO_FOLDER_SELECTED ||
                    NO_CONSUMPTION_SENSORS ||
                    NO_REPORT_SENSORS ||
                    isStep3
                  }
                  onClick={gotoNextStep}
                  sx={{ ml: 1 }}
                >
                  Weiter <ArrowRightTwoTone />
                </Button>
              )}

              {isStep3 && isAdHocMode && (
                <Button
                  color={'success'}
                  disabled={
                    !reportDate ||
                    !fId ||
                    NO_REPORT_SENSORS ||
                    newP.length === reportSensors.length
                  }
                  onClick={() => generateSelected(reportSensors)}
                  sx={{ ml: 1 }}
                >
                  Generieren
                </Button>
              )}

              {isStep3 && isAboMode && (
                <Button
                  color={'error'}
                  disabled={NO_ABO}
                  onClick={onDeleteAbo}
                  sx={{ ml: 1 }}
                >
                  Abo kündigen
                </Button>
              )}

              {isStep3 && isAboMode && (
                <Button
                  color={'success'}
                  disabled={NO_ABO_RECIPIENTS}
                  onClick={onUpdateAbo}
                  sx={{ ml: 1 }}
                >
                  {NO_ABO ? 'Abonnieren' : 'Aktualisieren'}
                </Button>
              )}
            </HStack>
          </Container>
        </KPage>
      )}
    </>
  )
}
